import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";
import {
  CustomFieldRecord,
  UpdateCustomFieldRequest,
} from "@/shared/domain/customFields";

import { customFieldQueryKeys } from "./customFieldQueryKeys";

interface UpdateCustomFieldArgs {
  fieldId: string;
  request: UpdateCustomFieldRequest;
}

/**
 * Updates a custom field's display_name/description in the current org. On
 * success it invalidates the cached lists for that field's entity type so the
 * edited row refreshes.
 */
export function useUpdateCustomField() {
  const { customFieldService } = useDomainServices();
  const queryClient = useQueryClient();
  const orgId = useCurrentOrgId();

  return useMutation<CustomFieldRecord, Error, UpdateCustomFieldArgs>({
    mutationFn: ({ fieldId, request }) =>
      customFieldService.updateCustomField(fieldId, request, { orgId }),
    onSuccess: (record) => {
      // Invalidate only the lists for this field's entity type, keyed off the
      // record's own org_id / entity_type, not the ambient org.
      void queryClient.invalidateQueries({
        queryKey: customFieldQueryKeys.listsForEntity(
          record.org_id,
          record.entity_type,
        ),
      });
    },
  });
}
